import * as React from 'react';
import {
  Animated,
  Easing,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  View
} from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import { translate } from 'secullum-i18n';
import { Text, getTestID, isTablet } from 'secullum-react-native-ui';
import { Amarelo100, Cinza100, Cinza80, getTheme } from '../../modules/layout';
import {
  DadosDesfazerRespostaQualidadeVidaTrabalho,
  TEMPO_DESFAZER
} from '../../modules/qualidadeVidaTrabalho';

interface Props {
  dados: DadosDesfazerRespostaQualidadeVidaTrabalho;
  insetInferior?: number;
  onDesfazer: () => void;
  onFechar: () => void;
}

class BarraRespostaQualidadeVidaTrabalho extends React.Component<Props> {
  progresso = new Animated.Value(1);

  componentDidMount() {
    const { fimPrazo, restanteCongelado } = this.props.dados;

    // A barra é remontada quando o aviso fecha, então a animação parte do que
    // resta do prazo, e não do início.
    const restante = restanteCongelado ?? Math.max(fimPrazo - Date.now(), 0);

    this.progresso.setValue(restante / TEMPO_DESFAZER);

    // Com o prazo congelado a barra já monta parada no ponto em que o Desfazer
    // foi acionado.
    if (restanteCongelado !== null) {
      return;
    }

    Animated.timing(this.progresso, {
      toValue: 0,
      duration: restante,
      easing: Easing.linear,
      useNativeDriver: true
    }).start();
  }

  // A barra já montada congela no lugar quando o Desfazer é acionado.
  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.dados.restanteCongelado === null &&
      this.props.dados.restanteCongelado !== null
    ) {
      this.progresso.stopAnimation();
    }
  }

  // A barra desmonta antes do prazo vencer em vários casos (desfazer, fechar,
  // nova inclusão, sair da tela), por isso paramos a animação aqui.
  componentWillUnmount() {
    this.progresso.stopAnimation();
  }

  render() {
    const { dados, onDesfazer, onFechar, insetInferior = 0 } = this.props;
    const { resposta } = dados;

    return (
      // Reivindicar o responder impede que o toque na barra chegue ao
      // TouchableWithoutFeedback do Modal e feche o aviso.
      <View
        nativeID="qvt-barra"
        testID={getTestID('qvt-barra')}
        style={[styles.barra, { bottom: insetInferior + 16 }]}
        onStartShouldSetResponder={() => true}
      >
        <Image
          nativeID="qvt-barra-imagem"
          testID={getTestID('qvt-barra-imagem')}
          source={resposta.imagem}
          style={styles.imagem}
        />
        <Text nativeID="qvt-barra-resposta" style={styles.texto}>
          {`${translate('Resposta')}: ${resposta.texto}`}
        </Text>
        <Pressable
          nativeID="qvt-barra-desfazer"
          testID={getTestID('qvt-barra-desfazer')}
          onPress={onDesfazer}
          style={({ pressed }) => [styles.acao, { opacity: pressed ? 0.7 : 1 }]}
        >
          <Text style={styles.acaoTexto}>{translate('Desfazer')}</Text>
        </Pressable>
        <Pressable
          nativeID="qvt-barra-fechar"
          testID={getTestID('qvt-barra-fechar')}
          onPress={onFechar}
          style={({ pressed }) => [
            styles.fechar,
            { opacity: pressed ? 0.7 : 1 }
          ]}
        >
          <FontAwesome
            name="times"
            size={isTablet() ? 22 : 18}
            color={theme.borderColor1}
          />
        </Pressable>

        <View style={styles.trilha}>
          <Animated.View
            style={[
              styles.progresso,
              { transform: [{ scaleX: this.progresso }] }
            ]}
          />
        </View>
      </View>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  barra: {
    // No web a barra precisa ficar presa à viewport, e não ao conteúdo do
    // ScrollView da tela. O `fixed` funciona no react-native-web, mas não existe
    // no tipo do React Native, daí o cast.
    position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute',
    left: 16,
    right: 16,
    maxWidth: 500,
    marginHorizontal: 'auto',
    backgroundColor: theme.backgroundColor3,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingLeft: 14,
    paddingRight: 6,
    paddingVertical: 22,
    elevation: 6,
    shadowColor: Cinza100,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 8
  },
  imagem: {
    height: isTablet() ? 36 : 30,
    width: isTablet() ? 36 : 30
  },
  texto: {
    flex: 1,
    color: theme.textColor4,
    fontFamily: theme.fontFamily1,
    fontSize: isTablet() ? 16 : 13.5
  },
  acao: {
    paddingHorizontal: 4,
    paddingVertical: 6
  },
  acaoTexto: {
    color: Amarelo100,
    fontFamily: theme.fontFamily1,
    fontSize: isTablet() ? 16 : 13.5
  },
  fechar: {
    paddingHorizontal: 9,
    paddingVertical: 4
  },
  trilha: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    backgroundColor: Cinza80,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: 'hidden'
  },
  progresso: {
    height: '100%',
    width: '100%',
    backgroundColor: Amarelo100,
    transformOrigin: 'left center'
  }
});

export default BarraRespostaQualidadeVidaTrabalho;
